/**
 * T-025 (W2-CONTRACTS §7) — saved views, tags, PATCH ticket, search query
 * self-contained เหมือน submodule อื่น (import แค่ zod) — index.ts ดึง TicketSort จากไฟล์นี้
 */
import { z } from "zod";

export const TicketSort = z.enum(["last_activity_desc", "created_desc", "due_asc", "priority_desc"]);
export type TicketSort = z.infer<typeof TicketSort>;

const ViewStatusType = z.enum(["open", "pending_customer", "pending_internal", "resolved", "closed"]);

export const SavedViewFilter = z.strictObject({
  q: z.string().max(200).optional(),
  status_type: z.array(ViewStatusType).max(5).optional(),
  team_id: z.uuid().optional(),
  view: z.enum(["mine", "team_unassigned", "team_all", "watching", "all"]).optional(),
});
export type SavedViewFilter = z.infer<typeof SavedViewFilter>;

export const SavedView = z.object({
  id: z.uuid(),
  owner_user_id: z.uuid(),
  team_id: z.uuid().nullable(),
  name: z.string(),
  icon: z.string().nullable(),
  filter: SavedViewFilter,
  sort: TicketSort,
  layout: z.enum(["list", "kanban", "calendar"]),
  kanban_status_set_id: z.uuid().nullable(),
  swimlane: z.string().nullable(),
  pinned: z.boolean(),
  shared: z.boolean(),
  position: z.number().int(),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
});
export type SavedView = z.infer<typeof SavedView>;

export const SavedViewInput = z.strictObject({
  name: z.string().trim().min(1).max(120),
  icon: z.string().max(32).nullable().optional(),
  filter: SavedViewFilter,
  sort: TicketSort.optional(),
  layout: z.enum(["list", "kanban", "calendar"]).optional(),
  kanban_status_set_id: z.uuid().nullable().optional(),
  swimlane: z.string().max(64).nullable().optional(),
  pinned: z.boolean().optional(),
  shared: z.boolean().optional(),
  team_id: z.uuid().nullable().optional(),
  position: z.number().int().min(0).max(9999).optional(),
});
export type SavedViewInput = z.infer<typeof SavedViewInput>;

export const SavedViewPatch = SavedViewInput.omit({ team_id: true }).partial();
export type SavedViewPatch = z.infer<typeof SavedViewPatch>;

export const Tag = z.object({
  id: z.uuid(),
  name: z.string(),
  color: z.string(),
  scope_type: z.enum(["global", "service", "team"]),
  scope_id: z.uuid().nullable(),
});
export type Tag = z.infer<typeof Tag>;

export const TagInput = z.strictObject({
  name: z.string().trim().min(1).max(64),
  color: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
  scope_type: z.enum(["global", "service", "team"]),
  scope_id: z.uuid().nullable().optional(),
});
export type TagInput = z.infer<typeof TagInput>;

export const TicketTagInput = z.strictObject({ name: z.string().trim().min(1).max(64) });
export type TicketTagInput = z.infer<typeof TicketTagInput>;

export const PatchTicket = z.strictObject({
  subject: z.string().trim().min(1).max(998).optional(),
  priority_code: z.string().min(1).max(32).optional(),
  due_at: z.iso.datetime().nullable().optional(),
  custom_fields: z.record(z.string(), z.unknown()).optional(),
  expected_version: z.number().int().min(1),
});
export type PatchTicket = z.infer<typeof PatchTicket>;

export const SearchQuery = z.strictObject({
  q: z.string().min(1).max(200),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().optional(),
});
export type SearchQuery = z.infer<typeof SearchQuery>;
